/** Small SVG circular progress ring — used for context-window usage. */
import { useT } from "@/i18n";
import { uiMessages } from "@/i18n/messages/ui";
import { cn } from "@/lib/cn";

interface ContextRingProps {
  percent: number; // 0..100
  size?: number; // px
  className?: string;
  "data-testid"?: string;
}

export function ContextRing({
  percent,
  size = 14,
  className,
  "data-testid": testId = "context-ring",
}: ContextRingProps) {
  const t = useT(uiMessages);
  const clamped = Math.min(100, Math.max(0, percent));
  const stroke = 2;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped / 100);
  const colorClass =
    clamped > 85
      ? "stroke-status-error"
      : clamped > 60
        ? "stroke-warning"
        : "stroke-accent";

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={cn("inline-block", className)}
      aria-label={t("contextUsed", { percent: Math.round(clamped) })}
      data-testid={testId}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        className="fill-none stroke-input-fill"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        className={cn(
          "fill-none transition-[stroke-dashoffset] duration-300",
          colorClass,
        )}
        strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}
